import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { MessageCircle, Send, ArrowLeft, User } from "lucide-react";

export default function MessagesPage() {
  const { token, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [members, setMembers] = useState([]);
  const [newConvoUser, setNewConvoUser] = useState("");
  const messagesEndRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/sporocila/pogovori`, { headers });
      setConversations(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchMessages = async (partnerId) => {
    try {
      const res = await axios.get(`${API}/sporocila/${partnerId}`, { headers });
      setMessages(res.data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { console.error(e); }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers });
      setMembers(res.data.filter(m => m.id !== user?.id).sort((a, b) => a.priimek.localeCompare(b.priimek, 'sl')));
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchConversations();
    fetchMembers();
    const interval = setInterval(fetchConversations, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      fetchMessages(selectedPartner.id);
      const interval = setInterval(() => fetchMessages(selectedPartner.id), 10000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartner]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedPartner) return;
    try {
      await axios.post(`${API}/sporocila`, {
        prejemnik_id: selectedPartner.id,
        vsebina: newMessage.trim()
      }, { headers });
      setNewMessage("");
      fetchMessages(selectedPartner.id);
      fetchConversations();
    } catch (e) {
      toast.error("Napaka pri pošiljanju");
    }
  };

  const startNewConvo = (userId) => {
    const m = members.find(x => x.id === userId);
    if (m) {
      setSelectedPartner({ id: m.id, ime: `${m.ime} ${m.priimek}` });
      setNewConvoUser("");
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString("sl-SI", { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString("sl-SI", { day: "numeric", month: "short" }) + " " +
           d.toLocaleTimeString("sl-SI", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="h-[calc(100vh-140px)]" data-testid="messages-page">
      <div className="flex h-full gap-4">
        {/* Conversations sidebar */}
        <Card className={`${selectedPartner ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 shrink-0`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Sporočila
            </CardTitle>
            <Select value={newConvoUser} onValueChange={startNewConvo}>
              <SelectTrigger data-testid="new-convo-select">
                <SelectValue placeholder="Nov pogovor..." />
              </SelectTrigger>
              <SelectContent>
                {members.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.priimek} {m.ime}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              {conversations.length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-8 px-4">Ni pogovorov. Izberite člana zgoraj za začetek.</p>
              ) : (
                <div className="space-y-0.5">
                  {conversations.map(c => (
                    <div
                      key={c.partner_id}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${
                        selectedPartner?.id === c.partner_id ? 'bg-muted' : ''
                      }`}
                      onClick={() => setSelectedPartner({ id: c.partner_id, ime: c.partner_ime })}
                      data-testid={`convo-${c.partner_id}`}
                    >
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm truncate">{c.partner_ime}</span>
                          {c.neprebrana > 0 && (
                            <Badge className="bg-red-500 text-white text-xs h-5 min-w-5 flex items-center justify-center">
                              {c.neprebrana}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{c.zadnje_sporocilo}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat area */}
        <Card className={`${selectedPartner ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
          {selectedPartner ? (
            <>
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSelectedPartner(null)}>
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{selectedPartner.ime}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-full p-4">
                  <div className="space-y-3">
                    {messages.map(m => {
                      const isMine = m.posiljatelj_id === user?.id;
                      return (
                        <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                            isMine 
                              ? 'bg-primary text-primary-foreground rounded-br-md' 
                              : 'bg-muted rounded-bl-md'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{m.vsebina}</p>
                            <p className={`text-[10px] mt-1 ${isMine ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                              {formatTime(m.ustvarjeno)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>
              <div className="p-3 border-t">
                <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Napiši sporočilo..."
                    data-testid="message-input"
                    className="flex-1"
                  />
                  <Button type="submit" disabled={!newMessage.trim()} data-testid="send-message-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <CardContent className="flex-1 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Izberite pogovor ali začnite novega</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
